/**
 * Manual food entry form. Ported from the original app's nutrition/manual/page.tsx —
 * same UI verbatim, with next/navigation → react-router and the api client.
 * Posts POST /api/nutrition/entries.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { MEAL_SLOT_KEYS, withDefaults } from '@/lib/meals';

// Serving units offered in the manual-entry dropdown. Kept in sync with the
// native apps (Swift/Kotlin) so an entry reads the same everywhere.
const SERVING_UNITS: Array<{ value: string; label: string }> = [
  { value: 'serving', label: 'serving(s)' },
  { value: 'g', label: 'grams (g)' },
  { value: 'oz', label: 'ounces (oz)' },
  { value: 'ml', label: 'milliliters (ml)' },
  { value: 'cup', label: 'cup(s)' },
  { value: 'tbsp', label: 'tablespoon(s)' },
  { value: 'tsp', label: 'teaspoon(s)' },
  { value: 'piece', label: 'piece(s)' },
  { value: 'slice', label: 'slice(s)' },
];

export default function NutritionManual() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [labels, setLabels] = useState<Record<string, string>>(withDefaults(undefined));
  const [slot, setSlot] = useState<string>('meal_1');

  useEffect(() => {
    api
      .get<{ mealLabels: Record<string, string> }>('/api/nutrition/meal-labels')
      .then((d) => setLabels(withDefaults(d.mealLabels)))
      .catch(() => {});
  }, []);
  const [calories, setCals] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [servings, setServings] = useState('1');
  const [servingUnit, setServingUnit] = useState('serving');
  const [saving, setSaving] = useState(false);

  // Macros are entered PER SERVING; the logged entry is per-serving × servings.
  const qty = parseFloat(servings) || 0;
  const perServing = {
    calories: parseFloat(calories) || 0,
    protein: parseFloat(protein) || 0,
    carbs: parseFloat(carbs) || 0,
    fat: parseFloat(fat) || 0,
  };
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const total = {
    calories: round1(perServing.calories * qty),
    protein: round1(perServing.protein * qty),
    carbs: round1(perServing.carbs * qty),
    fat: round1(perServing.fat * qty),
  };

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/nutrition/entries', {
        meal_slot: slot,
        food_name: name,
        // Logged macros are the TOTAL (per-serving × servings) so all the
        // dashboards/totals stay correct. serving_size/unit record the quantity
        // for display and future editing.
        calories: total.calories,
        protein_g: total.protein,
        carbs_g: total.carbs,
        fat_g: total.fat,
        serving_size: qty || null,
        serving_unit: qty ? servingUnit : null,
      });
      navigate('/nutrition');
    } catch { /* */ }
    finally { setSaving(false); }
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }}>
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: "1.9rem" }}>Manual Entry</h1>
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <label className="label">Food Name</label>
          <input type="text" className="input" placeholder="e.g. Chicken Breast" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div>
          <label className="label">Meal</label>
          <div className="flex flex-wrap gap-2">
            {MEAL_SLOT_KEYS.map((key) => (
              <button
                key={key}
                onClick={() => setSlot(key)}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                style={{ background: slot === key ? 'var(--gold)' : 'var(--surface)', color: slot === key ? 'var(--navy)' : 'var(--gray-400)' }}
              >
                {labels[key]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Servings</label>
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              className="input"
              style={{ flex: '0 0 40%' }}
              placeholder="1"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
            />
            <select
              className="input"
              style={{ flex: 1 }}
              value={servingUnit}
              onChange={(e) => setServingUnit(e.target.value)}
            >
              {SERVING_UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="card">
          <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gold-muted)' }}>Macros per serving</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Calories</label>
              <input type="number" className="input" placeholder="0" value={calories} onChange={(e) => setCals(e.target.value)} />
            </div>
            <div>
              <label className="label">Protein (g)</label>
              <input type="number" className="input" placeholder="0" value={protein} onChange={(e) => setProtein(e.target.value)} />
            </div>
            <div>
              <label className="label">Carbs (g)</label>
              <input type="number" className="input" placeholder="0" value={carbs} onChange={(e) => setCarbs(e.target.value)} />
            </div>
            <div>
              <label className="label">Fat (g)</label>
              <input type="number" className="input" placeholder="0" value={fat} onChange={(e) => setFat(e.target.value)} />
            </div>
          </div>

          {/* Live total: per-serving × servings (what actually gets logged). */}
          {qty !== 1 && (perServing.calories || perServing.protein || perServing.carbs || perServing.fat) ? (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--gray-400)' }}>
                Total for {servings || 0} {SERVING_UNITS.find((u) => u.value === servingUnit)?.label ?? servingUnit}
              </p>
              <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>
                {total.calories} kcal · {total.protein}p · {total.carbs}c · {total.fat}f
              </p>
            </div>
          ) : null}
        </div>

        <button onClick={handleSave} className="btn-gold w-full flex items-center justify-center gap-2" disabled={!name.trim() || saving}>
          <Check size={16} /> {saving ? 'Saving...' : 'Add Entry'}
        </button>
      </div>
    </div>
  );
}
